import _ from "lodash";
import React from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classes from "./ResourceList.scss";
import ResourceImagePlaceHolder from "UIComponents/ResourceImagePlaceHolder";
import I18nHOC from "UIComponents/I18nHOC";
import classNames from "classnames";
import { EmptyState, RadioButton } from "@toddle-edu/ds-web";
import {
  NoSearchInFilterIllustration,
  LEIllustraton,
} from "@toddle-edu/ds-theme";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import { ASSESSMENT_TYPE_AI_TUTOR } from "Constants/stringConstants";
import { onEnterOrSpaceKeyDown } from "Utils";
import { getIsNewGoalTaggingEnabled } from "Post/utils";

const BLANK_OBJECT = {};

const styles = {
  newImagePlaceholder: {
    borderRadius: "8px",
    height: "48px",
    width: "48px",
    flex: "inherit",
  },
  imagePlaceholder: {
    height: "64px",
    width: "80px",
    flex: "inherit",
  },
  emptyStateContainer: {
    minHeight: "600px",
  },
};
const emptyStateSubtitleStyle = {
  lineHeight: "24px",
};

const getEditAssessmentLabel = type => {
  switch (type) {
    case "fmt":
      return "FA";
    case "smt":
      return "SA";
    case "le":
      return "LE";
    case "qt":
      return "QT";
    case "worksheet":
      return "WS";
    case ASSESSMENT_TYPE_AI_TUTOR:
      return "AI";
  }
};

class ResourceList extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selectResourceId: "",
    };
    this.listContainerRef = React.createRef();
  }

  componentDidMount = () => {
    if (this.listContainerRef) {
      document.addEventListener("keydown", this.handleKeyPress);
    }
  };

  componentWillUnmount = () => {
    if (this.listContainerRef) {
      document.removeEventListener("keydown", this.handleKeyPress);
    }
  };

  renderResources = () => {
    const {
      resources,
      resourceId,
      onItemClicked,
      t,
      searchText,
      unitPlanOptionsLength,
      selectedUnitPlan,
      curriculumProgramType,
      getCurrentAssessmentTypes,
    } = this.props;

    const isNewGoalTaggingEnabled = getIsNewGoalTaggingEnabled();

    const onItemClickedLocal = resource => () => {
      onItemClicked(resource);
    };

    return _.get(resources, "length", 0) > 0 ? (
      _.map(resources, resource => {
        const assessmentType = _.get(resource, "assessmentType.value") || "le";
        const unitName = _.get(resource, "unitPlan.title.value") || "";
        const isSelected = resource.id === resourceId;
        const title = _.get(resource, "title.value")
          ? _.get(resource, "title.value")
          : "Untitled";
        const handleItemKeyDown = e => {
          onEnterOrSpaceKeyDown(e, () => {
            onItemClicked(resource);
          });
        };
        return (
          <div
            className={
              isSelected
                ? classes.singleItemContainerSelected
                : classes.singleItemContainer
            }
            key={_.get(resource, "id")}
            onClick={onItemClickedLocal(resource)}
            data-test-id="journal-postcreation-taglearningexperience-item"
            tabIndex={0}
            onKeyDown={handleItemKeyDown}
          >
            <div className={classes.itemAvatar}>
              <ResourceImagePlaceHolder
                id={resource.id}
                imageUrl={_.get(resource, "image.value", "")}
                imageThumbSize={56}
                containerStyle={styles.newImagePlaceholder}
              />
            </div>
            <div className={classes.itemDetailsContainer}>
              <div className={classes.resourceTitle}>{title}</div>
              {(unitName && isNewGoalTaggingEnabled) ||
              !isNewGoalTaggingEnabled ? (
                <div className={classes.resourceCentralIdea}>
                  {!isNewGoalTaggingEnabled &&
                    _.upperCase(getEditAssessmentLabel(assessmentType))}
                  {unitName ? (
                    <>
                      {!isNewGoalTaggingEnabled && (
                        <span className={classes.postListDot}></span>
                      )}
                      <span>{unitName}</span>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
            <RadioButton
              dsVersion="2.0"
              checked={isSelected}
              onClick={onItemClickedLocal(resource)}
              size="medium"
              tabIndex={-1}
            />
          </div>
        );
      })
    ) : _.get(searchText, "length", 0) > 0 ||
      _.get(selectedUnitPlan, "length", 0) >= 0 ? (
      <div className={classes.noDataContainer}>
        <EmptyState
          illustration={NoSearchInFilterIllustration}
          title={t("common:noResultHeading")}
          subtitle={t("common:no_items_matching_your_search")}
          subtitleStyle={emptyStateSubtitleStyle}
          size="small"
        />
      </div>
    ) : (
      <div className={classes.noDataContainer}>
        <EmptyState
          illustration={LEIllustraton}
          title={
            curriculumProgramType == "IB_PYP"
              ? t("journal:no_le_exist_unitplan")
              : t("journal:no_le_assessment")
          }
        />
      </div>
    );
  };

  renderOrDivider = () => {
    const { t } = this.props;
    return (
      <div className={classes.orContainer}>
        <div className={classes.orContentLine} />
        <div className={classes.orText}>
          {t("common:uppercase", { text: t("common:or") })}
        </div>
        <div className={classes.orContentLine} />
      </div>
    );
  };

  getTagLEOrAssessmentText = () => {
    const { assessmentCount, t } = this.props;
    if (assessmentCount > 0) {
      return t("journal:select_elements_tag_assessment");
    }
    return t("journal:select_elements_tag_le");
  };

  getElementNodes = containerRef => {
    if (!containerRef) {
      return [];
    }

    return Array.from(
      containerRef.querySelectorAll('div, [tabindex="0"]')
    ).filter(node => {
      return node.className.includes("singleItemContainer");
    });
  };

  stopPropagationAndPreventDefault = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  handleKeyPress = event => {
    const { key } = event;
    const elementNodes = this.getElementNodes(this.listContainerRef?.current);
    if (
      (key === "ArrowRight" || key === "d") &&
      !(document.activeElement.id === "search-le")
    ) {
      this.stopPropagationAndPreventDefault(event);
      const currentIndex = elementNodes.indexOf(document.activeElement);
      if (currentIndex < elementNodes.length - 1) {
        elementNodes[currentIndex + 1]?.focus();
      } else {
        _.first(elementNodes)?.focus();
      }
    }
    if (
      (key === "ArrowLeft" || key === "a") &&
      !(document.activeElement.id === "search-le")
    ) {
      this.stopPropagationAndPreventDefault(event);
      const currentIndex = elementNodes.indexOf(document.activeElement);
      if (currentIndex > 0) {
        elementNodes[currentIndex - 1]?.focus();
      } else {
        _.last(elementNodes)?.focus();
      }
    }
  };

  render() {
    const {
      isResourceSkipped,
      onResourceSkipClicked,
      t,
      networkStatus,
      resourceListRef,
      resources,
    } = this.props;
    const unitContainerClass = classNames({
      [classes.unitContainerSelected]: isResourceSkipped,
      [classes.unitContainer]: true,
    });

    return (
      <div className={classes.container} ref={resourceListRef}>
        <div
          tabIndex={-1}
          ref={this.listContainerRef}
          className={classes.listContent}
          style={
            _.isEmpty(resources) ? styles.emptyStateContainer : BLANK_OBJECT
          }
        >
          {this.renderResources()}
        </div>
      </div>
    );
  }
}

const mapActionCreators = {
  getCurrentAssessmentTypes,
};

export default compose(
  I18nHOC({ resource: ["journal", "common"] }),
  connect(null, mapActionCreators)
)(ResourceList);
